import * as React from 'react';
import classnames from 'classnames';
import { observer } from 'mobx-react';
import { configConsumer, ITranslationsCollection } from '@estee/elc-service';
import { StyledButton } from './ProductCTAStyles';
import {
    ADD_TO_BAG_BUTTON,
    ADD_TO_BAG_BUTTON_DISABLED
} from '../../constants/DataTestIdAttributes';
import { buttonLabels } from '../../utils/ProductCTALabels';
import { IAddToBagButton } from '../../interfaces/IProductCTA';
import ProductPrice from '../product-price/ProductPrice';
import { CtaTypes } from '~constants/CTATypes';
import { IPriceConfig, IProductsPrice } from '~interfaces/IProductPrice';

export const AddToBagButton = configConsumer([
    'addToBagDataTestId',
    'customAddToBagLabel',
    'quantity',
    'addToBag',
    'showIncludeTaxLabel'
])(
    observer(function AddToBagButton(props: IAddToBagButton) {
        const {
            addingToCart,
            butttonLabelOverride,
            config,
            disableAddToCart,
            getStickyButtonLabel,
            isShowingSuccessLabel,
            onAddToBagClick,
            selectedSku,
            componentType
        } = props;
        const { addToBagDataTestId, customAddToBagLabel, quantity, addToBag, showIncludeTaxLabel } =
            config;
        const isDisabled = !selectedSku.isShoppable || addingToCart || disableAddToCart;
        const translations = props.translations as ITranslationsCollection;
        const { addToBagButtonLabel, addedToCartLabel } = translations;

        const addToBagLabel = customAddToBagLabel || addToBagButtonLabel;
        const addToBagButtonLabels = buttonLabels(translations, addToBagLabel, addToBagLabel);

        const CTAButtonLabel = addToBagButtonLabels[selectedSku.inventoryStatus];
        const productCTAButtonLabel = isShowingSuccessLabel ? addedToCartLabel : CTAButtonLabel;

        const processedCtaLabel = getStickyButtonLabel(
            butttonLabelOverride || productCTAButtonLabel,
            CtaTypes.ADD_TO_BAG
        );

        const addToBagButtonClassNames = classnames({
            'elc-add-to-bag-button': true,
            'js-add-to-bag-button': true,
            'elc-add-to-bag-button-disabled': isDisabled,
            'js-add-to-bag-button-disabled': isDisabled
        });

        const addToBagButtonDataTestId = isDisabled
            ? ADD_TO_BAG_BUTTON_DISABLED
            : addToBagDataTestId || ADD_TO_BAG_BUTTON;

        const enablePriceOnAddToBag = addToBag?.price?.enabled;
        let priceProps: IProductsPrice = {
            priceRange: null,
            priceRangeNotDiscounted: null,
            isDiscounted: false,
            formattedOriginalPrice: null,
            formattedPrice: null,
            translations,
            selectedSku,
            componentType
        };

        if (enablePriceOnAddToBag) {
            const {
                showIncludeTaxLabel: showIncludeTaxLabelOverride,
                pricePerUnit: pricePerUnitOverride
            } = addToBag?.price as IPriceConfig;

            priceProps = {
                ...priceProps,
                showIncludeTaxLabel:
                    showIncludeTaxLabelOverride !== undefined
                        ? showIncludeTaxLabelOverride
                        : showIncludeTaxLabel,
                config:
                    pricePerUnitOverride !== undefined
                        ? {
                              productPricePerUnit: { enabled: pricePerUnitOverride }
                          }
                        : {}
            };
        }

        return (
            <StyledButton
                aria-label={productCTAButtonLabel}
                className={addToBagButtonClassNames}
                data-qty={quantity}
                data-test-id={addToBagButtonDataTestId}
                disabled={isDisabled}
                onClick={onAddToBagClick}
                title={productCTAButtonLabel}
            >
                {processedCtaLabel}
                {enablePriceOnAddToBag && <ProductPrice {...priceProps} />}
            </StyledButton>
        );
    })
);
